import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, Wrench, Truck, PackageCheck, XCircle, Loader2, Search, ArrowLeft, Package, Clock, FileText } from 'lucide-react';
import { publicApi } from '../lib/api';
import Header from '../components/Header';
import Footer from '../components/Footer';

const formatFCFA = (n) => new Intl.NumberFormat('fr-FR').format(n);

const STEPS = [
  { id: 'confirmed', label: 'Confirmée', icon: CheckCircle2 },
  { id: 'preparing', label: 'En préparation', icon: Wrench },
  { id: 'shipped', label: 'Expédiée', icon: Truck },
  { id: 'delivered', label: 'Livrée', icon: PackageCheck },
];

const STATUS_LABEL = {
  pending: 'En attente de paiement',
  confirmed: 'Confirmée',
  preparing: 'En préparation',
  shipped: 'Expédiée',
  delivered: 'Livrée',
  cancelled: 'Annulée'
};

const TrackOrderPage = () => {
  const { ref } = useParams();
  const [inputRef, setInputRef] = useState(ref || '');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async (reference) => {
    if (!reference) return;
    setLoading(true); setError('');
    try {
      const data = await publicApi.trackOrder(reference.toUpperCase().trim());
      setOrder(data);
    } catch (e) {
      setOrder(null);
      setError(e?.response?.data?.detail || 'Commande introuvable');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (ref) load(ref);
  }, [ref]);

  const currentStep = order ? STEPS.findIndex(s => s.id === order.status) : -1;
  const isCancelled = order?.status === 'cancelled';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-[#FAF7F2] to-orange-50/50 relative overflow-hidden">
        <div className="absolute -top-10 right-10 w-40 h-40 rotate-45 bg-[#F4A340]/10 rounded-3xl blur-2xl" />
        <div className="max-w-3xl mx-auto text-center relative">
          <Link to="/boutique" className="inline-flex items-center gap-2 text-slate-500 hover:text-[#E89534] text-sm font-medium mb-6">
            <ArrowLeft className="w-4 h-4" /> Retour à la boutique
          </Link>
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#F4A340]/10 text-[#E89534] text-xs font-semibold tracking-widest uppercase mb-4">
            Suivi de commande
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight mb-4">
            Où est ma <span className="text-[#E89534]">commande</span> ?
          </h1>
          <p className="text-slate-600 max-w-xl mx-auto">
            Entrez votre référence de commande pour voir son statut en temps réel.
          </p>

          <div className="mt-8 flex gap-2 max-w-md mx-auto">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={inputRef}
                onChange={(e) => setInputRef(e.target.value.toUpperCase())}
                placeholder="OR-XXXXXX"
                onKeyDown={(e) => e.key === 'Enter' && load(inputRef)}
                className="w-full pl-10 pr-4 py-3 rounded-full border-2 border-slate-200 focus:border-[#F4A340] outline-none font-mono uppercase"
              />
            </div>
            <button
              onClick={() => load(inputRef)}
              disabled={loading || !inputRef}
              className="px-6 py-3 bg-[#F4A340] hover:bg-[#E89534] disabled:bg-slate-200 text-white font-semibold rounded-full transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Suivre'}
            </button>
          </div>
        </div>
      </section>

      <section className="flex-1 py-12 px-6">
        <div className="max-w-3xl mx-auto">
          {error && (
            <div className="p-5 bg-red-50 border border-red-200 rounded-2xl text-red-700 text-center">
              <XCircle className="w-8 h-8 mx-auto mb-2" />
              {error}
            </div>
          )}

          {order && (
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Référence</div>
                    <div className="font-mono text-2xl font-bold text-slate-900 mt-1">{order.reference}</div>
                    <div className="text-sm text-slate-500 mt-1">Créée le {new Date(order.created_at).toLocaleDateString('fr-FR')}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Total</div>
                    <div className="text-2xl font-bold text-[#E89534] mt-1">{formatFCFA(order.total)} F</div>
                    <div className="text-xs text-slate-500 mt-1">{order.items_count} article{order.items_count > 1 ? 's' : ''}</div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <a
                    href={publicApi.invoiceUrl(order.reference)}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-full text-sm transition-colors"
                  >
                    <FileText className="w-4 h-4" /> Télécharger la facture PDF
                  </a>
                </div>
              </div>

              {/* Progress bar */}
              {!isCancelled && (
                <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-6">Progression</div>
                  <div className="flex items-center justify-between relative">
                    <div className="absolute top-6 left-6 right-6 h-1 bg-slate-100 rounded-full" />
                    <div
                      className="absolute top-6 left-6 h-1 bg-gradient-to-r from-[#F4A340] to-[#E89534] rounded-full transition-all duration-1000"
                      style={{ width: currentStep > 0 ? `calc(${(currentStep / (STEPS.length - 1)) * 100}% - 48px)` : '0' }}
                    />
                    {STEPS.map((s, i) => {
                      const Icon = s.icon;
                      const isActive = i <= currentStep;
                      const isCurrent = i === currentStep;
                      return (
                        <div key={s.id} className="relative flex flex-col items-center z-10">
                          <div className={`w-12 h-12 rounded-full border-4 flex items-center justify-center transition-all ${
                            isActive
                              ? 'bg-[#F4A340] border-[#F4A340] text-white shadow-lg'
                              : 'bg-white border-slate-200 text-slate-300'
                          } ${isCurrent ? 'scale-110 ring-4 ring-orange-100' : ''}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className={`text-xs mt-2 font-semibold uppercase tracking-wider ${
                            isActive ? 'text-slate-900' : 'text-slate-400'
                          }`}>{s.label}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-8 p-4 bg-orange-50 border border-orange-100 rounded-2xl text-center">
                    <div className="text-xs uppercase tracking-widest text-[#E89534] font-semibold">Statut actuel</div>
                    <div className="text-xl font-bold text-slate-900 mt-1">{order.status_label}</div>
                  </div>
                </div>
              )}

              {isCancelled && (
                <div className="bg-red-50 border border-red-200 rounded-3xl p-8 text-center">
                  <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                  <div className="text-xl font-bold text-red-900">Commande annulée</div>
                  <p className="text-sm text-red-700 mt-2">Contactez-nous pour toute question.</p>
                </div>
              )}

              {/* Timeline */}
              {order.tracking_history && order.tracking_history.length > 0 && (
                <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-4 flex items-center gap-2">
                    <Clock className="w-3 h-3" /> Historique
                  </div>
                  <ul className="space-y-4">
                    {[...order.tracking_history].reverse().map((h, i) => (
                      <li key={i} className="flex items-start gap-4">
                        <div className="w-3 h-3 rounded-full bg-[#F4A340] mt-1.5 flex-shrink-0 ring-4 ring-orange-100" />
                        <div className="flex-1 pb-4 border-b border-slate-100 last:border-0">
                          <div className="font-semibold text-slate-900">{STATUS_LABEL[h.status] || h.status}</div>
                          {h.note && <div className="text-sm text-slate-600 mt-1">{h.note}</div>}
                          <div className="text-xs text-slate-400 mt-1">{new Date(h.at).toLocaleString('fr-FR')}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {!order && !error && !loading && (
            <div className="text-center text-slate-400 py-12">
              <Package className="w-16 h-16 mx-auto mb-3 opacity-30" />
              <p>Entrez votre référence de commande pour voir son statut</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TrackOrderPage;
